/**
 * The headless M1 simulation (spec §4.2): one fixed 60 Hz `stepSim` that runs flight, weapons, projectiles,
 * torpedoes, Rapier, shields, death and respawn, targeting and locks, AI pilots and the wave director in the
 * spec order, deterministic for a seed (seeded RNG only). Runs in Node for tests and in the browser for play.
 *
 * Rules: per-tick code allocates nothing (D-014: typed arrays, registered queries and module scratch); world
 * positions are float64 and reach Rapier only through the physics origin (D-011, D-013). Spawning, death and
 * respawn create or remove Rapier bodies, which allocates inside Rapier's binding; they are rare events, never
 * per-tick work (accepted like Rapier's other binding allocations, D-024).
 *
 * M2 extension points (M2 plan §2.3, §2.4): five **stages** (`registerSimStage`) run registered functions after
 * steps 1, 3, 5, 6 and 8; `SimSystems.respawn` turns the M1 auto-respawn off; `respawnShipAt` and `setShipPose`
 * place ships; `removeFieldRock` takes a depleted rock out of the colliders (and every later rebuild). With no
 * stage registered and the default systems, a tick is exactly M1's.
 */
import {
  addComponent,
  addEntity,
  commitRemovals,
  createWorld,
  entityExists,
  registerQuery,
  removeEntity,
  type Query,
  type World,
} from 'bitecs'
import { createAiEventReader, type AiEventReader } from './ai/perception'
import { stepAIPilots, type AiDefs, type AiShips } from './ai/pilot'
import { createSpawnRequests, createWaveDirector, stepWaves, type InsideFieldFn, type SpawnRequests, type WaveDirector } from './ai/waves'
import type { ColliderData } from './assets/extract'
import { stepShieldRegen } from './combat/damage'
import { createEventRing, emitEvent, EV, eventSlot, firstReadable, type EventRing } from './combat/events'
import { isHostile, stepGrudges } from './combat/hostility'
import { stepLocks } from './combat/lock'
import { clearProjectiles, createProjectilePool, stepProjectiles, type ProjectilePool } from './combat/projectiles'
import { validateTargets } from './combat/targeting'
import { clearTorpedoes, createTorpedoPool, releaseTorpedoTarget, stepSecondaryWeapons, stepTorpedoes, type TorpedoPool } from './combat/torpedoes'
import { clearTurrets, resetTurrets, stepPointDefence, stepTurrets } from './combat/turrets'
import { stepPrimaryWeapons } from './combat/weapons'
import { SIM_DT } from './core/loop'
import { inverseRotateVec, rotateVec, type Quat, type Vec3 } from './core/math'
import { createRng, type Rng } from './core/rng'
import { loadGameData, type GameData } from './data'
import type { ShipDef } from './data/schemas'
import { resolveBoost, stepEnergy } from './flight/boost'
import { computeFlightForces, FlightDefScratch, loadFlightDef, type ControlInput, type FlightBody, type FlightForces } from './flight/flightModel'
import { createPhysics, createRayHit, createShipBody, readBody, recenterIfFar, removeShipBody, setBodyForces, stepWorld, type Physics, type RayHit, type RaycastFn } from './physics/physics'
import { createRaycaster } from './physics/raycast'
import { addFieldColliders, removeFieldColliders, removeStaticCollider, type StaticColliders } from './physics/statics'
import type { QualityPreset } from './render/contracts'
import { setKitName } from './ships/kitSlots'
import { fieldIndexOf, generateField, isInsideFieldEnvelope, type FieldLayout } from './world/field'
import {
  AIPilot,
  AngVel,
  Control,
  Energy,
  Hull,
  Life,
  Lock,
  MAX_ENTITIES,
  Player,
  Pos64,
  PrevPos64,
  PrevRot,
  Rot,
  Shield,
  Ship,
  Target,
  Team,
  Thrust,
  Vel,
  Weapons,
} from './world/components'

/** The data the sim reads every tick. The objects are the live (dev-panel tunable) runtime data. */
export type SimData = Pick<GameData, 'weapons' | 'aiProfiles' | 'waves' | 'field' | 'world'>

/**
 * Systems that can be switched off at runtime (the dev spawn menu freezes AI; tests that drive pilots or
 * weapons by hand turn the sim's own off). Everything else in the tick always runs.
 */
export interface SimSystems {
  /** Primary and secondary fire from `Control` (step 3). Projectiles and torpedoes in flight always advance. */
  weapons: boolean
  /** `stepAIPilots` (step 1). */
  ai: boolean
  /** The wave director (step 8). */
  waves: boolean
  /** The M1 respawn of the dead player at the spawn point (step 6). M2 career mode turns it off (its death flow replaces it). */
  respawn: boolean
}

/** The M2 sim stages in tick order (M2 plan §2.3; `SPACE_STAGES` in src/space/contracts.ts is the same list). */
export const SIM_STAGES = ['control', 'tools', 'physics', 'deaths', 'end'] as const
/**
 * Where a stage runs: `control` after step 1 (AI control), `tools` after step 3 (weapons, before the Rapier step),
 * `physics` after step 5 (body sync), `deaths` after step 6 (shields, deaths, respawn), `end` after step 8 (the
 * wave director), before the tick counter advances.
 */
export type SimStage = (typeof SIM_STAGES)[number]
/** A stage function: runs once per tick in its stage, allocation-free (D-014). */
export type SimStageFn = (sim: Sim) => void

/** Team id of the player side (spec §6; raiders are 1). */
export const PLAYER_TEAM = 0
export const RAIDER_TEAM = 1

/** Headless game state. */
export interface Sim {
  world: World
  physics: Physics
  defs: ShipDef[]
  data: SimData
  systems: SimSystems
  /** The seed of `rng` and of the field layout. */
  seed: number
  playerEid: number
  tick: number
  /** Sim time in seconds (tick × SIM_DT). */
  time: number
  /** Combat event ring; readers keep their own cursors. */
  events: EventRing
  /** Seeded sim RNG (determinism: the only randomness the sim uses). */
  rng: Rng
  /** Live ship query, registered once: bitECS `query()` allocates on every call (D-014). */
  ships: Query
  /** Ships with an `AIPilot`. */
  pilots: Query
  projectiles: ProjectilePool
  torpedoes: TorpedoPool
  /** Float64 world ray against the Rapier world (projectiles, torpedoes, AI avoidance). */
  raycast: RaycastFn
  waves: WaveDirector
  /** The director's output, turned into `spawnShip` calls in step 8. */
  spawnRequests: SpawnRequests
  /**
   * The asteroid field of the active preset (the one the colliders were built from): the renderer draws this
   * layout so every visible rock is solid (plan §2.5). Null when the sim was created without a preset.
   */
  field: FieldLayout | null
  fieldColliders: StaticColliders | null
  preset: QualityPreset | null
  /**
   * Collider per ship def index, from the loaded models (the app fills it; null → a ball of
   * `def.colliderRadius`). `spawnShip` uses it unless `SpawnOptions.collider` is given.
   */
  shipColliders: (ColliderData | null)[]
  /** `isInsideFieldEnvelope` bound to the live field data (wave spawns stay outside the field). */
  insideField: InsideFieldFn
  /** Internal: the AI's views of the sim, built once. */
  ai: { ships: AiShips; defs: AiDefs; events: AiEventReader }
  /** Internal: the collider each live entity was built with (respawn rebuilds the same body). */
  colliderOf: (ColliderData | null)[]
  /** Internal: first event sequence number not yet checked for `kill` by the death step. */
  killScanFrom: number
  /** Registered stage functions per stage, in registration order (`registerSimStage`). Created empty. */
  readonly stages: Readonly<Record<SimStage, SimStageFn[]>>
  /**
   * Stable ids (`FieldLayout.id`) of rocks gone from the world (depleted, fractured). They have no collider in
   * this or any later `rebuildFieldColliders`, and the field view skips them.
   */
  readonly removedRocks: Set<number>
  /** Bumped whenever `removedRocks` changes, so views rebucket at once. */
  fieldVersion: number
  /**
   * Additive (M3 R1): where the dead player respawns (float64 world). Initialised from `world.spawn.position`;
   * the Reach moves it to the home station's hail point. The respawn recentres the physics origin onto it first.
   */
  respawnPoint: Vec3
}

export interface SimOptions {
  /** RNG and field seed; defaults to `world.json` seed. */
  seed?: number
  /** Asteroid field preset: builds `field` and its colliders. Omitted → no field. */
  preset?: QualityPreset
  /** Runtime data; defaults to `loadGameData()` (parsed once per process). */
  data?: SimData
  /**
   * System switches. Default: weapons and respawn on; AI and waves on when a `preset` is given (the game world)
   * and off otherwise (bare sims in tests that drive pilots or spawn enemies themselves).
   */
  systems?: Partial<SimSystems>
}

let defaultData: SimData | undefined

function spawnPoint(data: SimData): Vec3 {
  const p = data.world.spawn.position
  return { x: p[0], y: p[1], z: p[2] }
}

export function createSim(defs: ShipDef[], opts: SimOptions = {}): Sim {
  const data = opts.data ?? (defaultData ??= loadGameData())
  const seed = opts.seed ?? data.world.seed
  const world = createWorld()
  const ships = registerQuery(world, [Ship])
  const pilots = registerQuery(world, [AIPilot])
  const physics = createPhysics()
  const events = createEventRing()
  const full = opts.preset !== undefined
  const waves = createWaveDirector(data.waves, data.aiProfiles)
  const sim: Sim = {
    world,
    physics,
    defs,
    data,
    systems: {
      weapons: opts.systems?.weapons ?? true,
      ai: opts.systems?.ai ?? full,
      waves: opts.systems?.waves ?? full,
      respawn: opts.systems?.respawn ?? true,
    },
    seed,
    playerEid: -1,
    tick: 0,
    time: 0,
    events,
    rng: createRng(seed),
    ships,
    pilots,
    projectiles: createProjectilePool(data.weapons.pools.projectiles),
    torpedoes: createTorpedoPool(data.weapons.pools.torpedoes),
    raycast: createRaycaster(physics),
    waves,
    spawnRequests: createSpawnRequests(Math.max(1, waves.maxShips)),
    field: null,
    fieldColliders: null,
    preset: null,
    shipColliders: defs.map(() => null),
    insideField: (x, y, z) => isInsideFieldEnvelope(data.field, x, y, z),
    ai: {
      ships: { all: ships.dense, pilots: pilots.dense },
      defs: { ships: defs, weapons: data.weapons, patrolCenter: spawnPoint(data) },
      events: createAiEventReader(events),
    },
    colliderOf: [],
    killScanFrom: 0,
    stages: { control: [], tools: [], physics: [], deaths: [], end: [] },
    removedRocks: new Set(),
    fieldVersion: 0,
    respawnPoint: spawnPoint(data),
  }
  if (opts.preset !== undefined) rebuildFieldColliders(sim, opts.preset)
  return sim
}

/**
 * Registers `fn` to run once per tick in `stage`, after the functions already registered there (M2 plan §2.3).
 * Returns the function that unregisters it (call it between ticks, never from inside a stage).
 */
export function registerSimStage(sim: Sim, stage: SimStage, fn: SimStageFn): () => void {
  const list = sim.stages[stage]
  list.push(fn)
  return (): void => {
    const i = list.indexOf(fn)
    if (i >= 0) list.splice(i, 1)
  }
}

/** Runs one stage's functions in registration order. Allocation-free. */
function runStage(sim: Sim, list: readonly SimStageFn[]): void {
  for (let i = 0; i < list.length; i++) list[i](sim)
}

/**
 * Builds the field layout for `preset` and replaces the field's static colliders with it (a quality change:
 * plan §2.5; call while paused). Deterministic: the layout depends only on the field data, the sim seed and
 * the preset, and presets nest (the rocks of Low are identical everywhere). Like any new collider, the rocks
 * block ships at once but become visible to raycasts only after the next Rapier step (Rapier refreshes its
 * scene queries inside `step()`).
 */
export function rebuildFieldColliders(sim: Sim, preset: QualityPreset): void {
  if (sim.fieldColliders) removeFieldColliders(sim.physics, sim.fieldColliders)
  sim.fieldColliders = null
  const layout = generateField(sim.data.field, sim.seed, preset, sim.ai.defs.patrolCenter)
  sim.field = layout
  sim.preset = preset
  sim.fieldColliders = addFieldColliders(sim.physics, layout, sim.data.field.colliderScale, sim.removedRocks)
}

/**
 * Takes the rock with stable id `rockId` out of the world: removes its collider from the current layout when it
 * is there, records the id in `removedRocks` (so no later `rebuildFieldColliders` brings it back) and bumps
 * `fieldVersion` (the field view rebuckets). Returns false when the rock was already removed. Rare event; like
 * any collider change it reaches raycasts after the next Rapier step.
 */
export function removeFieldRock(sim: Sim, rockId: number): boolean {
  if (sim.removedRocks.has(rockId)) return false
  sim.removedRocks.add(rockId)
  sim.fieldVersion++
  const layout = sim.field
  const set = sim.fieldColliders
  if (layout && set) {
    const i = fieldIndexOf(layout, rockId)
    if (i >= 0) removeStaticCollider(sim.physics, set, set.handles[i])
  }
  return true
}

/**
 * Brings every removed rock back (a new career, or a load that re-applies its own removals): clears
 * `removedRocks`, bumps `fieldVersion` and rebuilds the field colliders of the current preset.
 */
export function restoreFieldRocks(sim: Sim): void {
  if (sim.removedRocks.size === 0) return
  sim.removedRocks.clear()
  sim.fieldVersion++
  if (sim.preset !== null) rebuildFieldColliders(sim, sim.preset)
}

export interface SpawnOptions {
  /** 0 = player side, 1 = raiders. Default: 0 for the player, 1 otherwise. */
  team?: number
  /** AI profile index; adds an `AIPilot` when given. */
  profile?: number
  /** Asset collider (convex hulls or ball); null → ball(def.colliderRadius); absent → `sim.shipColliders[defIndex]`. */
  collider?: ColliderData | null
  /** Flight assist at spawn (default on). */
  faOn?: boolean
  /** Spawn rotation (default identity: nose along −Z). */
  rotation?: Quat
}

const SHIP_COMPONENTS = [Pos64, PrevPos64, Rot, PrevRot, Vel, AngVel, Control, Thrust, Ship, Energy, Team, Hull, Shield, Weapons, Target, Lock, Life]

/** Puts ship `eid` at (x, y, z) with rotation (qx, qy, qz, qw), at rest, with every stat full. Leaves `Control`, `Team` and `faOn` alone. */
function resetShip(eid: number, def: ShipDef, x: number, y: number, z: number, qx: number, qy: number, qz: number, qw: number): void {
  Pos64.x[eid] = PrevPos64.x[eid] = x
  Pos64.y[eid] = PrevPos64.y[eid] = y
  Pos64.z[eid] = PrevPos64.z[eid] = z
  Rot.x[eid] = PrevRot.x[eid] = qx
  Rot.y[eid] = PrevRot.y[eid] = qy
  Rot.z[eid] = PrevRot.z[eid] = qz
  Rot.w[eid] = PrevRot.w[eid] = qw
  Vel.x[eid] = Vel.y[eid] = Vel.z[eid] = 0
  AngVel.x[eid] = AngVel.y[eid] = AngVel.z[eid] = 0
  Thrust.surge[eid] = Thrust.sway[eid] = Thrust.heave[eid] = 0
  Thrust.pitch[eid] = Thrust.yaw[eid] = Thrust.roll[eid] = 0
  Ship.boosting[eid] = 0
  Energy.value[eid] = def.energy
  Hull.value[eid] = def.hull
  Shield.value[eid] = def.shield
  Shield.lastHit[eid] = -1e9
  Weapons.primaryIndex[eid] = 0
  Weapons.cooldownPrimary[eid] = 0
  Weapons.cooldownSecondary[eid] = 0
  Weapons.alternate[eid] = 0
  Weapons.torpedoes[eid] = def.torpedoRounds
  resetTurrets(eid)
  Target.eid[eid] = -1
  Lock.target[eid] = -1
  Lock.held[eid] = 0
  Lock.locked[eid] = 0
  Life.alive[eid] = 1
  Life.respawnAt[eid] = 0
}

/** Creates ship `eid`'s Rapier body at its ECS pose. */
function buildBody(sim: Sim, eid: number, def: ShipDef, collider: ColliderData | null): void {
  const body = createShipBody(sim.physics, eid, def, Pos64.x[eid], Pos64.y[eid], Pos64.z[eid], collider)
  if (Rot.x[eid] !== 0 || Rot.y[eid] !== 0 || Rot.z[eid] !== 0 || Rot.w[eid] !== 1) {
    rot.x = Rot.x[eid]
    rot.y = Rot.y[eid]
    rot.z = Rot.z[eid]
    rot.w = Rot.w[eid]
    body.setRotation(rot, true)
  }
  sim.colliderOf[eid] = collider
}

/** Spawns a ship at full stats (facing −Z unless `opts.rotation`) and emits `spawn`. The positional signature is M0's. */
export function spawnShip(sim: Sim, defIndex: number, x: number, y: number, z: number, isPlayer: boolean, opts: SpawnOptions = {}): number {
  const def = sim.defs[defIndex]
  if (!def) throw new Error(`Unknown ship def index ${defIndex}`)
  const eid = addEntity(sim.world)
  if (eid >= MAX_ENTITIES) {
    removeEntity(sim.world, eid)
    throw new Error(`Entity limit ${MAX_ENTITIES} reached`)
  }
  for (let i = 0; i < SHIP_COMPONENTS.length; i++) addComponent(sim.world, eid, SHIP_COMPONENTS[i])
  const q = opts.rotation
  resetShip(eid, def, x, y, z, q ? q.x : 0, q ? q.y : 0, q ? q.z : 0, q ? q.w : 1)
  Control.surge[eid] = Control.sway[eid] = Control.heave[eid] = 0
  Control.pitch[eid] = Control.yaw[eid] = Control.roll[eid] = 0
  Control.boost[eid] = Control.firePrimary[eid] = Control.fireSecondary[eid] = 0
  Ship.defIndex[eid] = defIndex
  Ship.faOn[eid] = opts.faOn === false ? 0 : 1
  Team.id[eid] = opts.team ?? (isPlayer ? PLAYER_TEAM : RAIDER_TEAM)
  sim.projectiles.shots[eid] = 0
  // Aim assist at the AI level; the menu feature sets the player's from the Aim assist setting (D-247).
  Weapons.assist[eid] = sim.data.weapons.aimAssist.levels[sim.data.weapons.aimAssist.aiLevel]
  AIPilot.target[eid] = -1 // a recycled id never carries a pilot's target into a crewed battery (M10 P5a7b, review M1)
  if (opts.profile !== undefined) {
    addComponent(sim.world, eid, AIPilot)
    AIPilot.state[eid] = 0
    AIPilot.profile[eid] = opts.profile
    AIPilot.target[eid] = -1
    AIPilot.stateTimer[eid] = 0
    AIPilot.jinkX[eid] = AIPilot.jinkY[eid] = 0
    AIPilot.aimX[eid] = AIPilot.aimY[eid] = AIPilot.aimZ[eid] = 0
    AIPilot.aimVX[eid] = AIPilot.aimVY[eid] = AIPilot.aimVZ[eid] = 0
    AIPilot.sampleTimer[eid] = 0
    AIPilot.lastAttacker[eid] = -1
  }
  const collider = opts.collider !== undefined ? opts.collider : (sim.shipColliders[defIndex] ?? null)
  try {
    buildBody(sim, eid, def, collider)
  } catch (err) {
    removeEntity(sim.world, eid)
    throw err
  }
  if (isPlayer) {
    addComponent(sim.world, eid, Player)
    sim.playerEid = eid
  }
  emitEvent(sim.events, EV.spawn, x, y, z, 0, 0, -1, eid, -1, 0, 0)
  return eid
}

/** Removes a ship: its Rapier body, its collider-map entries, torpedo pursuit of it, the entity and its kit name. */
export function despawnShip(sim: Sim, eid: number): void {
  removeShipBody(sim.physics, eid)
  releaseTorpedoTarget(sim.torpedoes, eid)
  sim.colliderOf[eid] = null
  if (entityExists(sim.world, eid)) removeEntity(sim.world, eid)
  if (sim.playerEid === eid) sim.playerEid = -1
  setKitName(eid, -1, null)
}

/**
 * Additive (M3 R1): takes ship `eid` out of Rapier for kinematic travel (cruise, lanes, the relay tunnel): its body
 * and collider-map entries go, while the entity stays alive with its `Pos64`, `Rot` and `Vel` (and the collider it
 * was built with, for `attachShip`). Flight and body sync skip it; the physics origin no longer follows it. A rare
 * event (it allocates inside Rapier's binding).
 */
export function detachShip(sim: Sim, eid: number): void {
  removeShipBody(sim.physics, eid)
}

/**
 * Additive (M3 R1): puts a detached, living ship back into Rapier at its `Pos64`/`Rot` with its collider, moving at
 * `Vel` with no spin. For the player (or with no player) it first recentres the physics origin onto the ship when
 * it is far (`recenterIfFar`), so the body never carries a large float32 offset. No-op for a ship that has a body or
 * is dead (respawn rebuilds a dead player).
 */
export function attachShip(sim: Sim, eid: number): void {
  if (!isDetached(sim, eid)) return
  if (eid === sim.playerEid || sim.playerEid < 0) recenterIfFar(sim.physics, Pos64.x[eid], Pos64.y[eid], Pos64.z[eid])
  buildBody(sim, eid, sim.defs[Ship.defIndex[eid]], sim.colliderOf[eid] ?? null)
  const rb = sim.physics.bodies[eid]
  if (!rb) return
  vel.x = Vel.x[eid]
  vel.y = Vel.y[eid]
  vel.z = Vel.z[eid]
  rb.setLinvel(vel, true)
  AngVel.x[eid] = AngVel.y[eid] = AngVel.z[eid] = 0
  angVel.x = angVel.y = angVel.z = 0
  rb.setAngvel(angVel, true)
}

/** Additive (M3 R1): true for a living ship without a Rapier body (detached for kinematic travel). */
export function isDetached(sim: Sim, eid: number): boolean {
  return eid >= 0 && entityExists(sim.world, eid) && Life.alive[eid] === 1 && sim.physics.bodies[eid] === undefined
}

/**
 * Additive (M3 R1): clears the local space for a system switch: despawns every ship except the player, frees every
 * projectile and torpedo, then commits the removals. A rare event (allocates).
 */
export function resetLocalSpace(sim: Sim): void {
  commitRemovals(sim.world)
  const dense = sim.ships.dense
  const doomed: number[] = []
  for (let i = 0; i < dense.length; i++) if (dense[i] !== sim.playerEid) doomed.push(dense[i])
  for (let i = 0; i < doomed.length; i++) despawnShip(sim, doomed[i])
  clearProjectiles(sim.projectiles)
  clearTorpedoes(sim.torpedoes)
  clearTurrets()
  commitRemovals(sim.world)
}

/**
 * Float scratch with its own maps (D-014): classes, not literals. `{ x, y, z }` and `{ surge, ... }` literals share
 * their maps with every literal of the same shape game-wide, so a field can turn tagged and each float store into it
 * then allocates a heap number.
 */
class Vec3Scratch implements Vec3 {
  x = 0
  y = 0
  z = 0
}
class QuatScratch implements Quat {
  x = 0
  y = 0
  z = 0
  w = 1
}
class ControlScratch implements ControlInput {
  surge = 0
  sway = 0
  heave = 0
  pitch = 0
  yaw = 0
  roll = 0
}

// Scratch objects for the hot loop (D-014).
const body: FlightBody = { vel: new Vec3Scratch(), angVel: new Vec3Scratch(), rot: new QuatScratch() }
const control: ControlInput = new ControlScratch()
const forces: FlightForces = { force: new Vec3Scratch(), torque: new Vec3Scratch() }
/** The current ship's flight fields in one map (D-1118): defs differ in key sets, which made the model's reads polymorphic. */
const flightDef = new FlightDefScratch()
const pos: Vec3 = new Vec3Scratch()
const rot: Quat = new QuatScratch()
const vel: Vec3 = new Vec3Scratch()
const angVel: Vec3 = new Vec3Scratch()
const local: Vec3 = new Vec3Scratch()
const hit: RayHit = createRayHit()
const deaths = new Int32Array(MAX_ENTITIES)
const playerPos: Vec3 = { x: 0, y: 0, z: 0 }
const playerFwd: Vec3 = { x: 0, y: 0, z: -1 }
const spawnRot: Quat = { x: 0, y: 0, z: 0, w: 1 }
const waveSpawn: SpawnOptions = { team: RAIDER_TEAM, profile: 0, rotation: spawnRot }
/** Respawn timing slack (s): `tick × SIM_DT` and `death time + delay` round differently. */
const TIME_EPS = 1e-9


function frac(v: number, limit: number): number {
  const f = v / limit
  return f > 1 ? 1 : f < -1 ? -1 : f
}

/**
 * Writes `Thrust` from this tick's world-space force and torque: each local axis over its limit.
 * Signs follow `Control` (+surge forward = local −Z, +yaw nose right, +roll right).
 */
function writeThrust(eid: number, def: ShipDef, boosting: boolean): void {
  inverseRotateVec(body.rot, forces.force.x, forces.force.y, forces.force.z, local)
  const forward = def.thrustForward * (boosting ? def.boostThrustMult : 1)
  Thrust.surge[eid] = local.z <= 0 ? frac(-local.z, forward) : frac(-local.z, def.thrustReverse)
  Thrust.sway[eid] = frac(local.x, def.thrustLateral)
  Thrust.heave[eid] = frac(local.y, def.thrustVertical)
  inverseRotateVec(body.rot, forces.torque.x, forces.torque.y, forces.torque.z, local)
  const inertia = def.mass * def.inertiaScale
  Thrust.pitch[eid] = frac(local.x, inertia * def.maxAngAccel.pitch)
  Thrust.yaw[eid] = frac(-local.y, inertia * def.maxAngAccel.yaw)
  Thrust.roll[eid] = frac(-local.z, inertia * def.maxAngAccel.roll)
}

/** Step 2: flight forces, `Thrust`, boost edge and energy for every ship with a body. */
function stepFlight(sim: Sim, ships: ArrayLike<number>): void {
  for (let i = 0; i < ships.length; i++) {
    const eid = ships[i]
    const rb = sim.physics.bodies[eid]
    if (!rb) continue
    const def = sim.defs[Ship.defIndex[eid]]
    const boosting = resolveBoost(Control.boost[eid] === 1, Ship.boosting[eid] === 1, Energy.value[eid])
    body.vel.x = Vel.x[eid]
    body.vel.y = Vel.y[eid]
    body.vel.z = Vel.z[eid]
    body.angVel.x = AngVel.x[eid]
    body.angVel.y = AngVel.y[eid]
    body.angVel.z = AngVel.z[eid]
    body.rot.x = Rot.x[eid]
    body.rot.y = Rot.y[eid]
    body.rot.z = Rot.z[eid]
    body.rot.w = Rot.w[eid]
    if (boosting && Ship.boosting[eid] === 0) {
      rotateVec(body.rot, 0, 0, -1, local)
      emitEvent(sim.events, EV.boostStart, Pos64.x[eid], Pos64.y[eid], Pos64.z[eid], local.x, local.y, local.z, eid, -1, 0, 0)
    }
    Ship.boosting[eid] = boosting ? 1 : 0
    control.surge = Control.surge[eid]
    control.sway = Control.sway[eid]
    control.heave = Control.heave[eid]
    control.pitch = Control.pitch[eid]
    control.yaw = Control.yaw[eid]
    control.roll = Control.roll[eid]
    computeFlightForces(body, control, loadFlightDef(def, flightDef), Ship.faOn[eid] === 1, boosting, SIM_DT, forces)
    writeThrust(eid, def, boosting)
    setBodyForces(sim.physics, rb, forces.force, forces.torque)
    Energy.value[eid] = stepEnergy(Energy.value[eid], boosting, def, SIM_DT)
  }
}

/** Step 5: Rapier bodies back into the ECS (`Pos64` = physics origin + translation). */
function syncBodies(sim: Sim, ships: ArrayLike<number>): void {
  for (let i = 0; i < ships.length; i++) {
    const eid = ships[i]
    const rb = sim.physics.bodies[eid]
    if (!rb) continue
    readBody(sim.physics, rb, pos, rot, vel, angVel)
    Pos64.x[eid] = pos.x
    Pos64.y[eid] = pos.y
    Pos64.z[eid] = pos.z
    Rot.x[eid] = rot.x
    Rot.y[eid] = rot.y
    Rot.z[eid] = rot.z
    Rot.w[eid] = rot.w
    Vel.x[eid] = vel.x
    Vel.y[eid] = vel.y
    Vel.z[eid] = vel.z
    AngVel.x[eid] = angVel.x
    AngVel.y[eid] = angVel.y
    AngVel.z[eid] = angVel.z
  }
}

/** True when a `kill` for `eid` was emitted since the death step last looked (the damage source reports it with the attacker). */
function killReported(sim: Sim, eid: number): boolean {
  const ring = sim.events
  for (let i = firstReadable(ring, sim.killScanFrom); i < ring.head; i++) {
    const s = eventSlot(i)
    if (ring.type[s] === EV.kill && ring.eid[s] === eid) return true
  }
  return false
}

/**
 * Step 6 death: every living ship at 0 hull explodes (`kill` unless its damage source already reported one,
 * then `explode` with the ship's radius). AI ships despawn; the player stays an entity (camera and views
 * survive) with `Life.alive = 0`, no body, and a respawn time.
 */
function resolveDeaths(sim: Sim, ships: ArrayLike<number>): void {
  let n = 0
  for (let i = 0; i < ships.length; i++) {
    const eid = ships[i]
    if (Life.alive[eid] === 1 && Hull.value[eid] <= 0) deaths[n++] = eid
  }
  for (let k = 0; k < n; k++) {
    const eid = deaths[k]
    const def = sim.defs[Ship.defIndex[eid]]
    const x = Pos64.x[eid]
    const y = Pos64.y[eid]
    const z = Pos64.z[eid]
    if (!killReported(sim, eid)) emitEvent(sim.events, EV.kill, x, y, z, 0, 0, 0, eid, -1, 0, 0)
    emitEvent(sim.events, EV.explode, x, y, z, 0, 0, 0, eid, -1, 0, def.colliderRadius)
    Life.alive[eid] = 0
    if (eid === sim.playerEid) {
      removeShipBody(sim.physics, eid)
      releaseTorpedoTarget(sim.torpedoes, eid)
      Life.respawnAt[eid] = sim.time + sim.data.world.spawn.respawnDelay
      Vel.x[eid] = Vel.y[eid] = Vel.z[eid] = 0
      AngVel.x[eid] = AngVel.y[eid] = AngVel.z[eid] = 0
      Thrust.surge[eid] = Thrust.sway[eid] = Thrust.heave[eid] = 0
      Thrust.pitch[eid] = Thrust.yaw[eid] = Thrust.roll[eid] = 0
      Ship.boosting[eid] = 0
    } else {
      despawnShip(sim, eid)
    }
  }
  sim.killScanFrom = sim.events.head
  // Despawned ships leave the queries now, so steps 7-9 never see them.
  if (n > 0) commitRemovals(sim.world)
}

/**
 * Respawns ship `eid` (dead or alive) at (x, y, z) with rotation `q`: every stat full, at rest, a fresh Rapier body
 * with the collider it was built with, and a `respawn` event (its direction is the new nose, local −Z). Leaves
 * `Control`, `Team` and `faOn` alone. A rare event (it rebuilds a body). For the player the physics origin is
 * recentred onto the point first (when far), so the new body never carries a large float32 offset (M3 R1: the
 * Reach's respawn point can be a whole system away).
 */
export function respawnShipAt(sim: Sim, eid: number, x: number, y: number, z: number, q: Readonly<Quat>): void {
  const def = sim.defs[Ship.defIndex[eid]]
  removeShipBody(sim.physics, eid)
  if (eid === sim.playerEid) recenterIfFar(sim.physics, x, y, z)
  resetShip(eid, def, x, y, z, q.x, q.y, q.z, q.w)
  buildBody(sim, eid, def, sim.colliderOf[eid] ?? null)
  // The identity keeps M1's exact event values (no -0 from rotating the nose).
  if (q.x === 0 && q.y === 0 && q.z === 0 && q.w === 1) {
    local.x = 0
    local.y = 0
    local.z = -1
  } else {
    rotateVec(q, 0, 0, -1, local)
  }
  emitEvent(sim.events, EV.respawn, x, y, z, local.x, local.y, local.z, eid, -1, 0, 0)
}

const IDENTITY: Readonly<Quat> = Object.freeze({ x: 0, y: 0, z: 0, w: 1 })

/**
 * Step 6 respawn (when `systems.respawn`): the dead player returns at `sim.respawnPoint` (M3 R1; initialised from
 * `world.spawn.position`, the Reach moves it) with full stats once its delay has run out.
 */
function resolveRespawn(sim: Sim): void {
  const p = sim.playerEid
  if (p < 0 || Life.alive[p] === 1 || sim.time + TIME_EPS < Life.respawnAt[p]) return
  const s = sim.respawnPoint
  respawnShipAt(sim, p, s.x, s.y, s.z, IDENTITY)
}

// Scratch for setShipPose (rare, but kept allocation-free like the tick).
const poseT: Vec3 = { x: 0, y: 0, z: 0 }
const poseQ: Quat = { x: 0, y: 0, z: 0, w: 1 }
const poseV: Vec3 = { x: 0, y: 0, z: 0 }
const ZERO_VEC: Readonly<Vec3> = Object.freeze({ x: 0, y: 0, z: 0 })

/**
 * Teleports live ship `eid` (docking, launch): float64 position (x, y, z), rotation `q`, linear velocity
 * (vx, vy, vz), angular velocity zero. Writes the body (translation relative to the physics origin, forces
 * cleared) and the ECS pose, including `PrevPos64`/`PrevRot` so render interpolation does not smear across the
 * jump, then recentres the physics origin when the player moved far (D-013). Call between ticks or from a stage.
 * Allocation-free.
 */
export function setShipPose(sim: Sim, eid: number, x: number, y: number, z: number, q: Readonly<Quat>, vx: number, vy: number, vz: number): void {
  Pos64.x[eid] = PrevPos64.x[eid] = x
  Pos64.y[eid] = PrevPos64.y[eid] = y
  Pos64.z[eid] = PrevPos64.z[eid] = z
  Rot.x[eid] = PrevRot.x[eid] = q.x
  Rot.y[eid] = PrevRot.y[eid] = q.y
  Rot.z[eid] = PrevRot.z[eid] = q.z
  Rot.w[eid] = PrevRot.w[eid] = q.w
  Vel.x[eid] = vx
  Vel.y[eid] = vy
  Vel.z[eid] = vz
  AngVel.x[eid] = AngVel.y[eid] = AngVel.z[eid] = 0
  const rb = sim.physics.bodies[eid]
  if (rb) {
    const o = sim.physics.origin
    poseT.x = x - o.x
    poseT.y = y - o.y
    poseT.z = z - o.z
    poseQ.x = q.x
    poseQ.y = q.y
    poseQ.z = q.z
    poseQ.w = q.w
    poseV.x = vx
    poseV.y = vy
    poseV.z = vz
    rb.setTranslation(poseT, true)
    rb.setRotation(poseQ, true)
    rb.setLinvel(poseV, true)
    rb.setAngvel(ZERO_VEC, true)
    rb.resetForces(true)
    rb.resetTorques(true)
  }
  if (eid === sim.playerEid) recenterIfFar(sim.physics, x, y, z)
}

/**
 * The carried-pose hook (M10 P5a9b2, plan 2026-10-17-m10-p5a P5a9b): pins detached ship `eid` (an S carried in its
 * L's cradle, `space/docking.ts`) to a float64 world pose (x, y, z), rotation `q` and velocity (vx, vy, vz: the
 * cradle pad's), once per tick after the body sync. Unlike `setShipPose` it keeps `PrevPos64`/`PrevRot` (this tick's
 * snapshot), so render interpolation follows the carrier smoothly, and it touches no Rapier body. A ship with a body
 * is left alone (returns false). Allocation-free (D-014).
 */
export function pinCarriedPose(sim: Sim, eid: number, x: number, y: number, z: number, q: Readonly<Quat>, vx: number, vy: number, vz: number): boolean {
  pinAt.x = x
  pinAt.y = y
  pinAt.z = z
  pinVel.x = vx
  pinVel.y = vy
  pinVel.z = vz
  return pinCarriedPoseFrom(sim, eid, pinAt, q, pinVel)
}
const pinAt: Vec3 = new Vec3Scratch()
const pinVel: Vec3 = new Vec3Scratch()

/** `pinCarriedPose` with the pose and velocity read from vectors (P5a11b: no floats across the per-tick call). */
export function pinCarriedPoseFrom(sim: Sim, eid: number, at: Readonly<Vec3>, q: Readonly<Quat>, v: Readonly<Vec3>): boolean {
  const x = at.x
  const y = at.y
  const z = at.z
  const vx = v.x
  const vy = v.y
  const vz = v.z
  if (sim.physics.bodies[eid] !== undefined) return false
  Pos64.x[eid] = x
  Pos64.y[eid] = y
  Pos64.z[eid] = z
  Rot.x[eid] = q.x
  Rot.y[eid] = q.y
  Rot.z[eid] = q.z
  Rot.w[eid] = q.w
  Vel.x[eid] = vx
  Vel.y[eid] = vy
  Vel.z[eid] = vz
  AngVel.x[eid] = AngVel.y[eid] = AngVel.z[eid] = 0
  return true
}

/** Step 8: the wave director; its requests become raider spawns facing the player. */
function stepWaveDirector(sim: Sim, ships: ArrayLike<number>): void {
  let enemies = 0
  for (let i = 0; i < ships.length; i++) {
    const eid = ships[i]
    if (Life.alive[eid] === 1 && isHostile(PLAYER_TEAM, Team.id[eid])) enemies++
  }
  const p = sim.playerEid
  if (p >= 0) {
    playerPos.x = Pos64.x[p]
    playerPos.y = Pos64.y[p]
    playerPos.z = Pos64.z[p]
    rot.x = Rot.x[p]
    rot.y = Rot.y[p]
    rot.z = Rot.z[p]
    rot.w = Rot.w[p]
    rotateVec(rot, 0, 0, -1, playerFwd)
  } else {
    const s = sim.data.world.spawn.position
    playerPos.x = s[0]
    playerPos.y = s[1]
    playerPos.z = s[2]
    playerFwd.x = 0
    playerFwd.y = 0
    playerFwd.z = -1
  }
  const req = sim.spawnRequests
  const started = stepWaves(sim.waves, enemies, sim.time, playerPos, playerFwd, sim.insideField, sim.rng, req, sim.events)
  if (started === 0) return
  for (let k = 0; k < req.count; k++) {
    spawnRot.x = req.qx[k]
    spawnRot.y = req.qy[k]
    spawnRot.z = req.qz[k]
    spawnRot.w = req.qw[k]
    waveSpawn.profile = req.profile[k]
    spawnShip(sim, req.defIndex[k], req.x[k], req.y[k], req.z[k], false, waveSpawn)
  }
}

/** One fixed 60 Hz tick in the spec §4.2 order. The player's `Control` must already be written for this tick. */
export function stepSim(sim: Sim): void {
  // Same result as query(world, [Ship]) (which commits removals first), without its per-call allocations.
  commitRemovals(sim.world)
  const ships = sim.ships.dense
  const data = sim.data
  const now = sim.time

  // 0. Snapshot for render interpolation.
  for (let i = 0; i < ships.length; i++) {
    const eid = ships[i]
    PrevPos64.x[eid] = Pos64.x[eid]
    PrevPos64.y[eid] = Pos64.y[eid]
    PrevPos64.z[eid] = Pos64.z[eid]
    PrevRot.x[eid] = Rot.x[eid]
    PrevRot.y[eid] = Rot.y[eid]
    PrevRot.z[eid] = Rot.z[eid]
    PrevRot.w[eid] = Rot.w[eid]
  }

  // 1. AI control (the player's was written by the app). M2 stage: control.
  if (sim.systems.ai) stepAIPilots(sim.ai.ships, sim.ai.defs, data.aiProfiles, sim.raycast, sim.ai.events, sim.rng, now, SIM_DT)
  runStage(sim, sim.stages.control)

  // 2. Flight forces, thrust output and energy.
  stepFlight(sim, ships)

  stepGrudges(now, sim.playerEid) // M4 I1: player-caused grudges expire; the damage hook learns the player.

  // 3. Weapons fire; projectiles and torpedoes advance and hit-test against the pre-step world (hits damage now).
  if (sim.systems.weapons) {
    stepPrimaryWeapons(ships, sim.defs, data.weapons, sim.projectiles, sim.events, now, SIM_DT, sim.rng)
    stepSecondaryWeapons(ships, sim.defs, data.weapons, sim.torpedoes, sim.events, SIM_DT)
    // M6 P20: turret mounts aim and fire themselves (player and AI ships alike), through the gun path.
    stepTurrets(ships, sim.defs, data.weapons, sim.projectiles, sim.torpedoes, sim.events, SIM_DT, sim.rng)
  }
  stepProjectiles(sim.projectiles, data.weapons, sim.raycast, sim.events, now, SIM_DT, hit)
  stepPointDefence(sim.projectiles, sim.torpedoes, data.weapons, sim.events, SIM_DT)
  stepTorpedoes(sim.torpedoes, ships, data.weapons, sim.raycast, sim.events, now, SIM_DT)
  // M2 stage: tools (before the Rapier step, so tool forces act this tick).
  runStage(sim, sim.stages.tools)

  // 4. Rapier. Step 9 (recenter) runs here, just before the step: Rapier refreshes its scene queries only
  // inside step(), so recentering after it would leave the next tick's raycasts against stale positions.
  // Only a player with a body leads the origin (M3 R1): a detached or dead player does not drag it around.
  const p = sim.playerEid
  if (p >= 0 && sim.physics.bodies[p] !== undefined) recenterIfFar(sim.physics, Pos64.x[p], Pos64.y[p], Pos64.z[p])
  stepWorld(sim.physics) // L-A2: `world.step()` with its JS bookkeeping only after bodies change.

  // 5. Body sync. M2 stage: physics.
  syncBodies(sim, ships)
  runStage(sim, sim.stages.physics)

  // 6. Shields (energy regenerates in step 2), death and respawn.
  for (let i = 0; i < ships.length; i++) {
    const eid = ships[i]
    if (Life.alive[eid] === 1 && Hull.value[eid] > 0) stepShieldRegen(eid, sim.defs[Ship.defIndex[eid]], SIM_DT, now)
  }
  resolveDeaths(sim, ships)
  if (sim.systems.respawn) resolveRespawn(sim)
  // M2 stage: deaths (reads this tick's `kill` events before any new spawn recycles an entity).
  runStage(sim, sim.stages.deaths)

  // 7. Targeting validation and torpedo locks.
  validateTargets(ships)
  stepLocks(ships, sim.defs, data.weapons, sim.events, SIM_DT)

  // 8. Wave director. M2 stage: end.
  if (sim.systems.waves) stepWaveDirector(sim, ships)
  runStage(sim, sim.stages.end)

  // 9. The physics recenter around the player ran before the Rapier step (see step 4).
  sim.tick++
  sim.time = sim.tick * SIM_DT
}
